import React, {useState, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {createSelector} from "reselect";
import { useTranslation, Trans } from 'react-i18next';
import {Icon} from "@blueprintjs/core";
import {isEmail} from "validator";
import i18n from "i18next";

// Libs
import {boxShadow, primaryColor} from "../common/libs/theme-lib";

// Components
import Button from "../common/components/button";
import PasswordInput from "../common/components/password-input";
import SelectInput from "../common/components/select-input";
import TextInput from "../common/components/text-input";
import VirtualizedTable from "../common/components/virtualized-table";

// Hooks
import {useAuth} from "../auth/auth";
import {LOGIN} from "../audit/audit-lib";

// Actions
import {readUser, updateUser, updateUserPassword} from "../users/users-action";
import {addNotification} from "../notifications/notifications-actions";
import {readEvents} from "../audit/audit-actions";

// Selectors
import {usersSelector, usersIsFetchingSelector, usersErrorSelector, usersUpdatedSelector} from "../users/users-selectors";
import {auditSelector} from "../audit/audit-selectors";
import {defaultLocale} from "../i18n";

// Component selector
const profilePageSelector = createSelector(
    usersSelector,
    usersIsFetchingSelector,
    usersErrorSelector,
    usersUpdatedSelector,
    auditSelector,
    (user, userIsFetching, userError, userUpdated, events) => {
        user = user.toJS();
        user = (typeof(user) === 'object' && user) || {};
        return {
            user: user,
            userIsFetching,
            userError,
            userUpdated,
            events: events?.toJS() || []
        }
    }
);

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        readUser: (userId) => dispatch(readUser(userId)),
        updateUser: (user) => dispatch(updateUser(user)),
        updateUserPassword: (user) => dispatch(updateUserPassword(user)),
        addNotification: (notification) => dispatch(addNotification(notification)),
        readEvents: (filters) => dispatch(readEvents(filters))
    }
}


const ProfilePage = (props) => {
    // Redux
    const {
        user, userIsFetching, userError, userUpdated, events,
    } = useSelector(state => profilePageSelector(state));
    const {readUser, updateUser, updateUserPassword, addNotification, readEvents} = useReduxDispatch();

    //i18n
    const {t} = useTranslation("profile");

    const auth = useAuth();
    const userId = auth.userId;

    // read user
    useEffect(() => {
        readUser(userId);
        readEvents({event: LOGIN, user: userId});
    }, []);

    useEffect(() => {
        // update values of form
        setName({value: user.name, isValid: true});
        setSurnames(user.surnames);
        setEmail({value: user.email, isValid: true});
        setSelectedLanguage(user.lang);
        setChangeForm(false);

        // error message notification
        if (userError) {
            if (userError.indexOf("Invalid password") >= 0) {
                // Create notification for update user password error
                addNotification({
                    message: t("toaster.error.updateUserPassword"),
                    color: 'red',
                    icon: 'cross'
                });
            }
        }

    }, [user, userError, userUpdated]);

    useEffect(async () => {
        await i18n.changeLanguage(user.lang);
    }, [userUpdated]);

    // State
    const [name, setName] = useState({value: user.name, isValid: true});
    const [surnames, setSurnames] = useState(user.surnames);
    const [email, setEmail] = useState({value: user.email, isValid: true});

    const langOptions = [
        {
            value: "es",
            label: t("langOptions.es")
        },
        {
            value: "en",
            label: t("langOptions.en")
        }
    ];

    // set language selected
    const [selectedLanguage, setSelectedLanguage] = useState(user.lang ? user.lang : langOptions[0].value);

    // user language selected
    const selectedLanguageData = langOptions.find(langOptions => langOptions.value === selectedLanguage);

    const [currentPassword, setCurrentPassword] = useState({value: "", isValid: false});
    const [password, setPassword] = useState({value: "", isValid: false});
    const [verifyPassword, setVerifyPassword] = useState({value: "", isValid: false});

    const [validInputs, setValidInputs] = useState({
        validLength: false,
        validLower: false,
        validUpper: false,
        validNumber: false,
        validSpecial: false
    });
    const [validForm, setValidForm] = useState(false);
    const [validUserForm, setValidUserForm] = useState(false);
    const [changeForm, setChangeForm] = useState(false);
    const [changePasswordForm, setChangePasswordForm] = useState(false);
    const [validValuePassword, setValidValuePassword] = useState(false);

    // password validation
    function validatePassword(password) {
        const validLength = password.length >= 6;
        const validLower = password.search(/[a-z]/) !== -1;
        const validUpper = password.search(/[A-Z]/) !== -1;
        const validNumber = password.search(/[0-9]/) !== -1;
        const validSpecial = password.search(/[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/) !== -1;

        setValidInputs({validLength: validLength, validLower: validLower, validUpper: validUpper, validNumber: validNumber, validSpecial: validSpecial});

        const isValid = validLength && validUpper && validLower && validNumber && validSpecial;

        setPassword({value: password, isValid: isValid});
        setChangePasswordForm(true);
    }

    function valueVerifyPassword(value) {
        setVerifyPassword({value: value});
        setChangePasswordForm(true);
    }

    function validateValuePassword() {
        password.value === verifyPassword.value ? setValidValuePassword(true) : setValidValuePassword(false);
    }

    function validateForm() {
        password.isValid && (password.value === verifyPassword.value) && currentPassword.value.length >= 1 ?
            setValidForm(true) : setValidForm(false);
    }

    // validate email
    function validateEmail(email) {
        let isValid = !!isEmail(email);
        setEmail({value: email, isValid: isValid});
    }

    function validateName(name) {
        name.length >= 1 ? setName({value: name, isValid: true}) : setName({value: name, isValid: false});
    }

    function validateCurrentPassword(currentPassword) {
        currentPassword.length >= 1 ? setCurrentPassword({value: currentPassword, isValid: true}) : setCurrentPassword({value: currentPassword, isValid: false});
    }

    function validateUserForm() {
        email.isValid && name.isValid && changeForm ? setValidUserForm(true) : setValidUserForm(false);
    }

    useEffect(() => {
        validateForm();
        validateValuePassword();
    }, [password, verifyPassword, currentPassword]);

    useEffect(() => {
        validateUserForm();
        setChangeForm(true);
    }, [email, name, surnames, selectedLanguage]);

    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            flex: 1
        }}>
            <div style={{
                display: "flex",
                flexDirection: "column",
                paddingBottom: "15px"
            }}>
                <div style={{
                    color: primaryColor,
                    fontSize: 28,
                    fontWeight: "bold",
                    marginBottom: 10
                }}>{t("title")}</div>
                <div style={{fontWeight: 100, fontSize: 18}}>
                    {user.type === "admin" && (
                        <Trans
                            i18nKey={"profile:adminDescription"}
                            values={{email: user.email}}
                            components={[<strong>string</strong>]}
                        />
                    )}
                    {user.type === "client" && (
                        <Trans
                            i18nKey={"profile:clientDescription"}
                            values={{email: user.email, client: user.client.name}}
                            components={[<strong>string</strong>]}
                        />
                    )}
                </div>
            </div>
            <div style={{
                backgroundColor: "white",
                boxShadow: boxShadow,
                padding: 15,
                display: "flex",
                marginBottom: "15px"
            }}>
                <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                    flex: 1
                }}>
                    <div>
                        <TextInput
                            label={t("inputs.name")}
                            style={{marginRight: 15, width: 200}}
                            value={name.value}
                            setValue={validateName}
                        />
                        {!name.isValid && (
                            <span style={errorStyle}>El nombre es obligatorio</span>
                        )}
                    </div>
                    <TextInput
                        label={t("inputs.surnames")}
                        style={{marginRight: 15, width: 200}}
                        value={surnames}
                        setValue={setSurnames}
                    />
                    <div style={{marginRight: 15, maxWidth: 400, width: "100%"}}>
                        <TextInput
                            label={t("inputs.email")}
                            value={email.value}
                            setValue={validateEmail}
                        />
                        {!email.isValid && (
                            <span style={errorStyle}>El email no es válido</span>
                        )}
                    </div>
                    <SelectInput
                        options={langOptions}
                        label={t("inputs.lang")}
                        onChange={(selectedLanguage) => setSelectedLanguage(selectedLanguage.value)}
                        value={selectedLanguageData}
                        style={{...inputStyle}}
                    />
                </div>
                <div style={{
                    display: "flex",
                    alignItems: "flex-end"
                }}>
                    <Button
                        text={t("action.save")}
                        action={() => {
                            updateUser({
                                _id: user._id,
                                name: name.value,
                                surnames: surnames,
                                email: email.value,
                                lang: selectedLanguageData.value
                            });
                        }}
                        disabled={validUserForm}
                        intent="primary"
                    />
                </div>
            </div>
            <div style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between"
            }}>
                <div style={{
                    backgroundColor: "white",
                    boxShadow: boxShadow,
                    padding: 15,
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    width: "calc(50% - 15px)"
                }}>
                    <div style={{
                        display: "flex",
                        flexDirection: "column"
                    }}>
                        <PasswordInput
                            label={t("inputs.currentPassword")}
                            style={loginItemStyle}
                            value={currentPassword.value}
                            setValue={validateCurrentPassword}
                        />
                        {!!changePasswordForm && currentPassword.value.length <= 1 && password.value.length >= 1 &&  (
                            <span style={errorStyle}>{t("validations.currentPassword")}</span>
                        )}
                        <PasswordInput
                            label={t("inputs.newPassword")}
                            style={loginItemStyle}
                            value={password.value}
                            setValue={validatePassword}
                        />
                        {!password.isValid && password.value.length >= 1 && (
                            <span style={errorStyle}>{t("validations.password")}</span>
                        )}
                        <PasswordInput
                            label={t("inputs.verifyNewPassword")}
                            style={loginItemStyle}
                            value={verifyPassword.value}
                            setValue={valueVerifyPassword}
                        />
                        {!validValuePassword && verifyPassword.value.length >= 1 && (
                            <span style={errorStyle}>{t("validations.verifyPassword")}</span>
                        )}
                    </div>
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        justifyContent: "space-between"
                    }}>
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            marginTop: "15px"
                        }}>
                            <div style={{
                                display: "flex"
                            }}>
                                <div style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    width: 20,
                                    height: 20,
                                    border: "1px solid black",
                                    borderRadius: 25,
                                    marginRight: 10
                                }}>
                                    <Icon icon="high-priority" iconSize={12} style={{margin: "auto"}}/>
                                </div>
                                <span>{t("requirements.title")}</span>
                            </div>
                            <ul>
                                <li style={liStyle}>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between"
                                    }}>
                                        <div>
                                            {t("requirements.characters")}
                                        </div>
                                        <div>
                                            {validInputs.validLength && (
                                                <Icon icon="tick" iconSize={16} />
                                            )}
                                        </div>
                                    </div>
                                </li>
                                <li style={liStyle}>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between"
                                    }}>
                                        <div>
                                            {t("requirements.lower")}
                                        </div>
                                        <div>
                                            {validInputs.validLower && (
                                                <Icon icon="tick" iconSize={16} />
                                            )}
                                        </div>
                                    </div>
                                </li>
                                <li style={liStyle}>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between"
                                    }}>
                                        <div>
                                            {t("requirements.upper")}
                                        </div>
                                        <div>
                                            {validInputs.validUpper && (
                                                <Icon icon="tick" iconSize={16} />
                                            )}
                                        </div>
                                    </div>
                                </li>
                                <li style={liStyle}>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between"
                                    }}>
                                        <div>
                                            {t("requirements.number")}
                                        </div>
                                        <div>
                                            {validInputs.validNumber && (
                                                <Icon icon="tick" iconSize={16} />
                                            )}
                                        </div>
                                    </div>
                                </li>
                                <li style={liStyle}>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between"
                                    }}>
                                        <div>
                                            {t("requirements.special")}
                                        </div>
                                        <div>
                                            {validInputs.validSpecial && (
                                                <Icon icon="tick" iconSize={16} />
                                            )}
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        </div>
                        <Button
                            intent="primary"
                            text={t("action.changePassword")}
                            disabled={validForm}
                            action={() => {
                                updateUserPassword({
                                    _id: user._id,
                                    currentPassword: currentPassword.value,
                                    newPassword: password.value,
                                    verifyPassword: verifyPassword.value
                                });
                            }}
                        />
                    </div>
                </div>
                <div style={{
                    backgroundColor: "white",
                    boxShadow: boxShadow,
                    padding: 15,
                    display: "flex",
                    flexDirection: "column",
                    width: "calc(50% - 15px)"
                }}>
                    <strong style={{padding: "4px 10px"}}>{t("lastAccess")}</strong>
                    {events && (
                        <VirtualizedTable
                            data={events}
                            config={{
                                sortable: true,
                                initialState: {sortBy: [{id: "date", desc: true}]}
                            }}
                            columns={[
                                {
                                    id: "date",
                                    Header: t("date"),
                                    accessor: "date",
                                    type: "datetime",
                                }
                            ]}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;

const inputStyle = {
    marginRight: "30px",
    width: "200px"
};

const liStyle = {
    marginTop: "5px",
    width: "200px"
}

const loginItemStyle = {
    margin: "15px 0 0 0",
    display: "flex",
    flexDirection: "column",
    width: "200px"
}

const errorStyle = {
    color: primaryColor,
    fontSize: 12
}